import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableFooter from "@mui/material/TableFooter";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import LinkIcon from "@mui/icons-material/Link";
import {
  DailyExpenseRoute,
  ExpenseCategory,
} from "../../types/daily-expense.type";
import { formatNumber } from "../../utils";
import SalesAmountCell from "./SalesAmountCell";
import { PREFILL_TEXT, UNIT_TEXT } from "./constants";
import {
  DailyExpenseFormState,
  DailyExpenseFormTotals,
  cellKey,
} from "./dailyExpenseForm";
import {
  BODY_ROW_SX,
  FOOT_ROW_SX,
  HEAD_ROW_SX,
  TABLE_SX,
  stickyCellSx,
} from "./tableStyles";

const ROUTE_COLUMN_MIN_WIDTH = 170;
const AMOUNT_COLUMN_MIN_WIDTH = 120;
const LEGEND_DOT_SIZE = 8;
const NO_ERRORS = new Set<string>();

interface SalesExpenseTableProps {
  routes: DailyExpenseRoute[];
  columns: ExpenseCategory[];
  sales: DailyExpenseFormState["sales"];
  totals: DailyExpenseFormTotals;
  readOnly: boolean;
  /** cellKey()s to outline in red; none until the user tries to save. */
  errorCells?: Set<string>;
  /** Absent on a read-only table (the head-office drawer). */
  onChangeCell?: (
    routeUuid: string,
    categoryCode: string,
    text: string,
  ) => void;
  onBlurCell?: (routeUuid: string, categoryCode: string) => void;
}

/**
 * ส่วนที่ 1 · ฝ่ายขาย: one row per route that ran, one column per sales category, totals both ways.
 * The S2 form and the S3 drawer share it. สาย and รวม stay pinned when a narrow container (the
 * drawer) scrolls the amounts sideways, so a route's total is always in view.
 */
export default function SalesExpenseTable({
  routes,
  columns,
  sales,
  totals,
  readOnly,
  errorCells = NO_ERRORS,
  onChangeCell,
  onBlurCell,
}: Readonly<SalesExpenseTableProps>) {
  return (
    <Paper>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        flexWrap="wrap"
        mb={1.5}
      >
        <Stack direction="row" alignItems="baseline" gap={1.25}>
          <Typography fontWeight={600}>1 · ฝ่ายขาย</Typography>
          <Typography variant="body2" color="text.secondary">
            ต่อสาย · {UNIT_TEXT}
          </Typography>
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          gap={2}
          sx={{ color: "text.secondary" }}
        >
          <Stack direction="row" alignItems="center" gap={0.75}>
            <LinkIcon color="primary" fontSize="small" />
            <Typography variant="caption">{PREFILL_TEXT}</Typography>
          </Stack>
          <Stack direction="row" alignItems="center" gap={0.75}>
            <Box
              sx={{
                width: LEGEND_DOT_SIZE,
                height: LEGEND_DOT_SIZE,
                borderRadius: "50%",
                bgcolor: "warning.main",
              }}
            />
            <Typography variant="caption">แก้ทับแล้ว</Typography>
          </Stack>
        </Stack>
      </Stack>
      <TableContainer
        sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}
      >
        <Table size="small" sx={TABLE_SX}>
          <TableHead>
            <TableRow sx={HEAD_ROW_SX}>
              <TableCell
                sx={{
                  ...stickyCellSx("left"),
                  zIndex: 2,
                  minWidth: ROUTE_COLUMN_MIN_WIDTH,
                }}
              >
                สาย
              </TableCell>
              {columns.map((column) => (
                <TableCell
                  key={column.code}
                  align="right"
                  sx={{ minWidth: AMOUNT_COLUMN_MIN_WIDTH }}
                >
                  {column.name}
                </TableCell>
              ))}
              <TableCell
                align="right"
                sx={{ ...stickyCellSx("right"), zIndex: 2 }}
              >
                รวม
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {routes.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={columns.length + 2}
                  align="center"
                  sx={{ color: "text.secondary" }}
                >
                  ไม่มีสายที่วิ่งวันนี้
                </TableCell>
              </TableRow>
            )}
            {routes.map((route) => (
              <TableRow key={route.route_uuid} sx={BODY_ROW_SX}>
                <TableCell sx={stickyCellSx("left")}>
                  {route.route_code}-{route.route_name}
                </TableCell>
                {columns.map((column) => (
                  <TableCell key={column.code} sx={{ px: 0.75 }}>
                    <SalesAmountCell
                      routeUuid={route.route_uuid}
                      categoryCode={column.code}
                      label={`${route.route_code} ${column.name}`}
                      cell={sales[route.route_uuid][column.code]}
                      hasError={errorCells.has(
                        cellKey(route.route_uuid, column.code),
                      )}
                      readOnly={readOnly}
                      onChange={onChangeCell}
                      onBlur={onBlurCell}
                    />
                  </TableCell>
                ))}
                <TableCell
                  align="right"
                  sx={{ ...stickyCellSx("right"), fontWeight: 600 }}
                >
                  {formatNumber(totals.routeTotals[route.route_uuid])}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow sx={FOOT_ROW_SX}>
              <TableCell sx={{ left: 0, zIndex: 2 }}>รวม</TableCell>
              {columns.map((column) => (
                <TableCell key={column.code} align="right">
                  {formatNumber(totals.columnTotals[column.code])}
                </TableCell>
              ))}
              <TableCell align="right" sx={{ right: 0, zIndex: 2 }}>
                {formatNumber(totals.salesTableTotal)}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Paper>
  );
}
